import { ALL_QUEUES, type JobStatus } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const ALL = '__all__';
const STATUSES: JobStatus[] = [
  'QUEUED',
  'WAITING',
  'ACTIVE',
  'DELAYED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
];
const STATUS_VARIANT: Record<JobStatus, 'success' | 'destructive' | 'warning' | 'info' | 'muted'> =
  {
    COMPLETED: 'success',
    FAILED: 'destructive',
    ACTIVE: 'info',
    DELAYED: 'warning',
    QUEUED: 'muted',
    WAITING: 'muted',
    CANCELLED: 'muted',
  };

export function AdminJobsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const params = {
    queue: queue === ALL ? undefined : queue,
    status: status === ALL ? undefined : status,
    limit: 100,
  };
  const jobs = useQuery({
    queryKey: queryKeys.admin.jobs(params),
    queryFn: () => api.admin.jobs(params),
  });
  const act = useMutation({
    mutationFn: ({
      queue,
      id,
      action,
    }: {
      queue: string;
      id: string;
      action: 'retry' | 'remove' | 'promote';
    }) => api.admin.jobAction(queue, id, action),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] }),
    onError: (e) => toastError(e),
  });

  return (
    <AdminPage
      section="jobs"
      actions={
        <Button
          variant="outline"
          size="sm"
          loading={jobs.isFetching}
          onClick={() => void jobs.refetch()}
        >
          <RefreshCw aria-hidden /> {t('admin.refresh')}
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={queue} onValueChange={setQueue}>
          <SelectTrigger aria-label={t('admin.jobs.queue')} className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin.jobs.allQueues')}</SelectItem>
            {ALL_QUEUES.map((q) => (
              <SelectItem key={q} value={q}>
                {q}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label={t('admin.jobs.status')} className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin.jobs.allStatuses')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`enums.jobStatus.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {jobs.isLoading && <Spinner label={t('app.loading')} />}
      {jobs.error && <Alert variant="destructive">{describeError(jobs.error)}</Alert>}
      {jobs.data && (
        <ResponsiveTable
          caption={t('admin.sections.jobs')}
          rowKey={(j) => `${j.queue}:${j.id}`}
          rows={jobs.data}
          empty={t('common.noResults')}
          columns={[
            {
              key: 'id',
              header: t('admin.jobs.job'),
              cell: (j) => (
                <span className="font-mono text-xs">
                  {j.queue} / {j.name} #{j.id}
                </span>
              ),
            },
            {
              key: 'status',
              header: t('admin.jobs.status'),
              cell: (j) => (
                <Badge variant={STATUS_VARIANT[j.status]}>{t(`enums.jobStatus.${j.status}`)}</Badge>
              ),
            },
            { key: 'attempts', header: t('admin.jobs.attempts'), cell: (j) => j.attemptsMade },
            {
              key: 'progress',
              header: t('admin.jobs.progress'),
              cell: (j) => (j.progress != null ? `${j.progress}%` : '–'),
            },
            {
              key: 'created',
              header: t('common.created'),
              cell: (j) => formatDate(j.createdAt, i18n.language, true) || '–',
            },
            {
              key: 'reason',
              header: t('admin.jobs.failedReason'),
              cell: (j) => (
                <span className="line-clamp-2 max-w-xs text-xs text-destructive">
                  {j.failedReason ?? ''}
                </span>
              ),
            },
            {
              key: 'actions',
              header: '',
              label: t('admin.actions'),
              cell: (j) => (
                <span className="flex flex-wrap justify-end gap-1">
                  {j.status === 'FAILED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act.mutate({ queue: j.queue, id: j.id, action: 'retry' })}
                    >
                      {t('admin.jobs.retry')}
                    </Button>
                  )}
                  {j.status === 'DELAYED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act.mutate({ queue: j.queue, id: j.id, action: 'promote' })}
                    >
                      {t('admin.jobs.promote')}
                    </Button>
                  )}
                  {j.status !== 'ACTIVE' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => act.mutate({ queue: j.queue, id: j.id, action: 'remove' })}
                    >
                      {t('admin.jobs.remove')}
                    </Button>
                  )}
                </span>
              ),
            },
          ]}
        />
      )}
    </AdminPage>
  );
}
